import recordAuthor from 'utils/recordAuthor';
/* eslint-disable no-continue */
import React, {
  createContext, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useFirestore } from 'firebaseHooks/FirestoreContext';
import { useAuth } from 'firebaseHooks/AuthContext';
import useGlobal from 'global-state/store';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { PENDING_PREFIX } from '../utils/pendingUpdates';
import { addError, clearErrorsForUpdateId, readPublicationErrors } from '../utils/inventoryErrors';
import { checkInventoryStorage, createOutbox } from '../utils/queue/outbox';
import { createQueueEngine } from '../utils/queue/engine';
import { createFirestoreTransport } from '../utils/queue/firestoreTransport';

export const InventoryQueueRegistryContext = createContext(null);

export function InventoryQueueRegistryProvider({ children }) {
  const firestore = useFirestore();
  const { currentUser } = useAuth();
  const [globalState, globalActions] = useGlobal();
  const { t } = useTranslation();
  const organizationId = globalState.activeOrganization;
  const userId = currentUser?.uid;
  const canSync = !!userId && !!organizationId && globalState.userStatus === 'setupDone';
  const engineRef = useRef(null);
  const recoverRef = useRef(null);
  const storageReady = useRef(false);
  const [storageStatus, setStorageStatus] = useState('checking');
  const feedback = useRef({ globalActions, t });
  feedback.current = { globalActions, t };
  const [localBatches, setLocalBatches] = useState([]);
  const [queueReady, setQueueReady] = useState(false);
  const [queueError, setQueueError] = useState(null);

  const report = useCallback((error, context = {}) => {
    if (!context.inventoryId) setQueueError(error.message);
    // A rejected local save stays in the form; there is no queued item to retry.
    if (context.inventoryId && context.type !== 'staging') {
      addError(context.inventoryId, { ...context, message: error.message });
    }
    Sentry.captureException(error, { tags: { area: 'inventoryQueue' }, extra: context });
    feedback.current.globalActions.setSnackbarMessage({
      message: feedback.current.t('operation.error_queue'), severity: 'error',
    });
  }, []);

  useEffect(() => {
    storageReady.current = false;
    setStorageStatus('checking');
    setLocalBatches([]);
    setQueueReady(false);
    setQueueError(null);
    if (!canSync) return undefined;
    let stopped = false;
    const channel = typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('inventory-outbox');
    const scope = JSON.stringify([firestore.app.options.projectId, userId, organizationId]);
    let engine;
    let migrationComplete = false;
    let recovery = null;
    const errorWatches = new Map();
    const transport = createFirestoreTransport(firestore, organizationId);
    const recoverPublicationErrors = (batches) => {
      readPublicationErrors().forEach(({ inventoryId, updateId }) => {
        const key = JSON.stringify([inventoryId, updateId]);
        if (errorWatches.has(key) || batches.some((batch) => batch.id === updateId)) return;
        const stop = transport.watchPublished({ inventoryId, id: updateId }, () => {
          if (stopped) return;
          clearErrorsForUpdateId(inventoryId, updateId);
          errorWatches.get(key)?.();
          errorWatches.delete(key);
        }, () => {
          // No server proof (offline, permissions, etc.): preserve the error.
          errorWatches.get(key)?.();
          errorWatches.delete(key);
        });
        errorWatches.set(key, stop);
      });
    };
    const refresh = async () => {
      await engine.refresh();
      const batches = await store.list();
      if (!stopped) {
        setLocalBatches(batches); setQueueReady(migrationComplete);
        if (migrationComplete) recoverPublicationErrors(batches);
      }
    };
    const reportActive = (error) => { if (!stopped) report(error); };
    const changed = () => {
      if (!stopped) { channel?.postMessage(scope); refresh().catch(reportActive); }
    };
    const store = createOutbox(scope, changed);
    engine = createQueueEngine({
      store,
      transport,
      ownerId: uuidv4(),
      makeId: uuidv4,
      onError: report,
      onPublished: (batch) => clearErrorsForUpdateId(batch.inventoryId, batch.id),
    });
    engineRef.current = engine;
    if (channel) channel.onmessage = (event) => { if (event.data === scope) refresh().catch(reportActive); };

    // Import the previous localStorage format before removing it. The original
    // records remain in Firestore's persistent write queue; never recreate them.
    const recover = () => {
      if (stopped) return Promise.resolve();
      // Focus, online and the button can arrive together: share one recovery.
      if (recovery) return recovery;
      recovery = (async () => {
        if (!storageReady.current) {
          try {
            await checkInventoryStorage();
          } catch (error) {
            if (!stopped) {
              setStorageStatus('unavailable');
              report(error, { type: 'storage' });
            }
            return;
          }
        }
        if (stopped) return;
        storageReady.current = true;
        setStorageStatus('ready');
        migrationComplete = false;
        setQueueReady(false);
        try {
          let migrationError;
          const keys = Object.keys(localStorage).filter((key) => key.startsWith(PENDING_PREFIX));
          for (const key of keys) {
            if (stopped) return;
            try {
              const raw = localStorage.getItem(key);
              const pending = JSON.parse(raw);
              if (pending?.organizationId && pending.organizationId !== organizationId) continue;
              if (!pending?.recordIds?.length) continue;
              // eslint-disable-next-line no-await-in-loop
              await store.importLegacy(key.slice(PENDING_PREFIX.length), pending);
              if (localStorage.getItem(key) === raw) localStorage.removeItem(key);
            } catch (error) { migrationError = migrationError || error; }
          }
          if (stopped) return;
          migrationComplete = !migrationError;
          // Resume already imported batches even if another entry could not import.
          await engine.retry();
          await refresh();
          if (migrationError) throw migrationError;
          if (!stopped) setQueueError(null);
        } catch (error) { reportActive(error); }
      })().finally(() => { recovery = null; });
      return recovery;
    };
    recoverRef.current = recover;
    recover();
    // Hints only wake recovery. They never select batch membership or force an
    // offline/online branch. Firestore acknowledgements alone release batches.
    const retry = () => {
      if (document.visibilityState === 'hidden') return;
      recover();
    };
    window.addEventListener('online', retry);
    window.addEventListener('focus', retry);
    document.addEventListener('visibilitychange', retry);
    return () => {
      stopped = true;
      engine.dispose();
      errorWatches.forEach((stop) => stop());
      errorWatches.clear();
      if (engineRef.current === engine) engineRef.current = null;
      if (recoverRef.current === recover) recoverRef.current = null;
      channel?.close();
      window.removeEventListener('online', retry);
      window.removeEventListener('focus', retry);
      document.removeEventListener('visibilitychange', retry);
    };
  }, [firestore, organizationId, userId, canSync, report]);

  const enqueueRecord = useCallback(async (inventoryId, payload) => {
    try {
      if (!storageReady.current) throw new Error('Local storage is unavailable');
      if (!engineRef.current) throw new Error('Inventory queue is not initialized');
      // Return the LOCAL commit, never the server acknowledgement, to the form.
      return await engineRef.current.enqueue(inventoryId, {
        ...payload, ...recordAuthor({ uid: userId }), timeStamp: new Date(),
      });
    } catch (error) {
      report(error, { inventoryId, type: 'staging', containerId: payload.containerId });
      throw error;
    }
  }, [report, userId]);

  const flush = useCallback(() => recoverRef.current?.() || Promise.resolve(), []);

  const value = useMemo(() => ({
    enqueueRecord, flush, localBatches, queueReady, queueError, storageStatus,
  }), [enqueueRecord, flush, localBatches, queueReady, queueError, storageStatus]);
  return <InventoryQueueRegistryContext.Provider value={value}>{children}</InventoryQueueRegistryContext.Provider>;
}

export function useInventoryQueueRegistry() {
  return React.useContext(InventoryQueueRegistryContext);
}
